import axios, { AxiosResponse } from 'axios';
import type { ValueOf } from 'type-fest';
import { captureException } from 'utils/exception';

import { Codeowners } from 'components/Common/Error/Codeowners';
import { LegacyFeature } from 'components/PayWall/FeaturePayWall/types';
import { PayWallOrgFeature, PayWallVariant } from 'components/PayWall/Redux/Reducer.type';

import RestAPI from '../../Common/restApi';
import { GetPlansResponse, PayWallPaymentSummary, PayWallPlanSummary } from '../types';

export type UpdatePlanPreviewQueryArg = {
  additional_users?: number | undefined;
  plan_type: string;
  addon_features: string;
  billing_plan_id: number;
};

export type UpdatePlanParams = {
  payment_plan: string;
  billing_plan_id: number;
  addon_features: PayWallOrgFeature[];
  additional_users: number;
  charge_after_plan_change: boolean;
};

export type UpdatePlanAndPaymentParams = {
  stripe_card_token: string;
  payment_plan: string;
  billing_plan_id: number;
  addon_features: PayWallOrgFeature[];
  additional_users: number;
};

export const PAYWALL_PLANS_WITH_GROUPS_ENDPOINT_VERSION = '3';

export default class PayWallApi {
  static updatePlan(data: UpdatePlanParams) {
    return RestAPI.postWithFormData<PayWallPaymentSummary, UpdatePlanParams>('/pro/account/update_billing_info', data);
  }

  static freezeBilling(organizationId: string) {
    return RestAPI.postWithFormData(`/admin/api/organizations/${organizationId}/billing_plans/freeze_billing`);
  }

  static async updateAddon(data?: LegacyFeature) {
    try {
      const response: AxiosResponse<Record<string, never>> = await RestAPI.postWithFormData(
        '/pro/account/update_addon',
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      captureException(error, Codeowners.BILLING);
      throw new Error('Ooops, there was an error updating this feature.');
    }
  }

  static fetchUpdatePlanPreview(data: UpdatePlanPreviewQueryArg) {
    return RestAPI.getWithFormData<PayWallPlanSummary, UpdatePlanPreviewQueryArg>(
      '/pro/account/update_plan_preview',
      data
    );
  }

  static fetchUpdateAddonPreview(data?: LegacyFeature) {
    if (!data) {
      throw new Error('No feature given for fetching addon preview');
    }

    return RestAPI.getWithFormData('/pro/account/update_addon_preview', data);
  }

  static fetchPlanData(version?: string, variant?: ValueOf<typeof PayWallVariant>, orgId?: number) {
    let headers;

    if (version) {
      headers = { 'requested-version': version };
    }

    switch (variant) {
      case PayWallVariant.MaxModal:
        if (!orgId) {
          throw new Error('Organization ID not defined for fetching Max Modal plans');
        }

        return RestAPI.getWithHeaders<GetPlansResponse>(
          `/admin/api/organizations/${orgId}/available_billing_plans`,
          headers
        );
      default:
        return RestAPI.getWithHeaders<GetPlansResponse>('/pro/pay_wall/plans', headers);
    }
  }

  static sendEmailReceipt(billId: string) {
    return RestAPI.post(`/pro/account/send_email_receipt/${billId}`);
  }
}
